import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleAuthProvider, getMultiFactorResolver, signInWithPopup, type MultiFactorError, type MultiFactorResolver } from 'firebase/auth';
import { auth } from '../../lib/firebase';
import { useT } from '../../lib/i18n';
import { TotpChallenge } from './totp';

/**
 * Continue with Google, on both doors.
 *
 * The point is not the convenience, it is the verification: Google asserts
 * the address, Firebase records `emailVerified: true`, and every guard in
 * the product — the route redirects, the verify screen's own bounce, the
 * server's `requireVerifiedEmail` — reads that same flag. A Google user
 * never sees the code screen and never waits on a mail that might be in
 * spam, which is exactly the failure the code path had on launch morning.
 *
 * One account per address holds: signing in with Google on an address that
 * registered with a password resolves to the same account, with Google
 * attached.
 */
export function GoogleButton({ next }: { next: string }) {
  const t = useT();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mfa, setMfa] = useState<MultiFactorResolver | null>(null);

  const go = async () => {
    setError(null);
    setBusy(true);
    try {
      const provider = new GoogleAuthProvider();
      // Always offer the chooser: on a shared machine the previous account
      // silently reused is the wrong account signed in.
      provider.setCustomParameters({ prompt: 'select_account' });
      await signInWithPopup(auth, provider);
      navigate(next, { replace: true });
    } catch (err) {
      const code = (err as { code?: string }).code ?? '';
      if (code === 'auth/multi-factor-auth-required') {
        setMfa(getMultiFactorResolver(auth, err as MultiFactorError));
        setBusy(false);
        return;
      }
      // Closing the popup is a decision, not a failure.
      if (code !== 'auth/popup-closed-by-user' && code !== 'auth/cancelled-popup-request') {
        setError(code === 'auth/popup-blocked' ? t('auth.googlePopupBlocked') : t('auth.googleFailed'));
      }
      setBusy(false);
    }
  };

  if (mfa) {
    return <TotpChallenge resolver={mfa} onDone={() => navigate(next, { replace: true })} />;
  }

  return (
    <div className="flex flex-col gap-3">
      <button
        type="button"
        onClick={go}
        disabled={busy}
        className="flex h-10 w-full items-center justify-center gap-2.5 rounded-panel border border-line-strong bg-transparent text-body font-medium transition-colors duration-fast ease-emphasis hover:bg-canvas-tint disabled:cursor-not-allowed disabled:opacity-50"
      >
        <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
          <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92a8.78 8.78 0 0 0 2.68-6.62Z" />
          <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.32A9 9 0 0 0 9 18Z" />
          <path fill="#FBBC05" d="M3.97 10.72a5.41 5.41 0 0 1 0-3.44V4.96H.96a9 9 0 0 0 0 8.08l3-2.32Z" />
          <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59A9 9 0 0 0 .96 4.96l3 2.32C4.68 5.16 6.66 3.58 9 3.58Z" />
        </svg>
        {t('auth.google')}
      </button>
      {error && (
        <p role="alert" className="text-meta-sm text-alert">
          {error}
        </p>
      )}
      <div className="flex items-center gap-3" aria-hidden>
        <span className="h-px flex-1 bg-line" />
        <span className="text-meta-sm text-faint">{t('auth.orContinue')}</span>
        <span className="h-px flex-1 bg-line" />
      </div>
    </div>
  );
}
